import {
  isOneOnOneItem,
  isSelfStudyItem,
  periodToString,
  PricingModel,
  PricingModelItem,
  PricingModelType,
} from "../../model/pricingmodel";
import { HStack, VStack } from "../Stack";
import { Elements } from "@stripe/react-stripe-js";
import { ReactComponent as ArrowBackIcon } from "images/arrow_left.svg";
import AddNewPaymentMethod from "./AddNewPaymentMethod";
import { useStyles } from "../../pages/Settings/components/PlanDetail/const";
import { FormControlLabel, Radio, RadioGroup } from "@material-ui/core";
import { loadStripe } from "@stripe/stripe-js";
import Configuration from "../../config";
import { PaymentMethod } from "../../model/paymentMethod";
import ExistingPaymentMethods from "./ExistingPaymentMethod";
import { ReactNode, useMemo, useState } from "react";
import { updateUser } from "../../services/authUtil";
import Action from "../formElements/Action/Action";
import ComparePlansModal from "../ComparePlansModal";
import { pluralize } from "../../services/util";
import { ReactComponent as CheckIcon } from "images/check_icon.svg";
import ExpandableWhatIncludedButton from "./ExpandableButton";
import TalaeraRoutes from "../routes/TalaeraRoutes";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useUser from "hooks/useUser";
import "./style.css";

const stripePromise = loadStripe(Configuration.STRIPE_KEY);

interface PlanSelectorProps {
  pricingModel: PricingModel;
  selectedPlan: PricingModelItem;
  setSelectedPlan: (item: PricingModelItem) => void;
}

function PlanSelector({
  pricingModel,
  selectedPlan,
  setSelectedPlan,
}: PlanSelectorProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const hdChangePlan = (e: any) => {
    if (e.target?.value) {
      const plan = pricingModel.items.find(item => item.id === e.target.value);
      if (plan) {
        setSelectedPlan(plan);
      }
    }
  };

  const selfStudyRadioGroup = pricingModel.items.filter(isSelfStudyItem).map(item => (
    <HStack
      className={"price-item " + (selectedPlan.id === item.id ? "active" : "")}
      justify={"space-between"}
      events={{ onClick: () => setSelectedPlan(item) }}
      key={item.id}
    >
      <FormControlLabel
        value={item.id}
        control={
          <Radio
            className={classes.root}
            disableRipple
            color="default"
            checkedIcon={<span className={`${classes.icon} ${classes.checkedIcon}`} />}
            icon={<span className={classes.icon} />}
          />
        }
        label={
          "Billed " +
          (item.length === 30 || item.length === 31
            ? "Monthly"
            : item.length === 365
              ? "Yearly"
              : `every ${item.length} days`)
        }
      />
      <div className={"price-tag"}>
        <b>${item.price}</b>/{periodToString(item.length!)}
      </div>
    </HStack>
  ));

  const oneOnOneRadioGroup = pricingModel.items.filter(isOneOnOneItem).map(item => (
    <HStack
      className={"price-item " + (selectedPlan.id === item.id ? "active" : "")}
      justify={"space-between"}
      events={{ onClick: () => setSelectedPlan(item) }}
      key={item.id}
    >
      <FormControlLabel
        value={item.id}
        control={
          <Radio
            className={classes.root}
            disableRipple
            color="default"
            checkedIcon={<span className={`${classes.icon} ${classes.checkedIcon}`} />}
            icon={<span className={classes.icon} />}
          />
        }
        label={`${item.number} ${pluralize(t("Common.Session"), t("Common.Sessions"), item.number)}`}
      />
      <div className={"price-tag"}>
        <b>${item.price}</b>
      </div>
    </HStack>
  ));

  return (
    <RadioGroup
      defaultValue={selectedPlan?.id}
      value={selectedPlan?.id}
      onChange={hdChangePlan}
    >
      {selfStudyRadioGroup}
      {oneOnOneRadioGroup}
    </RadioGroup>
  );
}

interface UpgradePlanProps {
  pricingModel: PricingModel;
  selectedPlan: PricingModelItem;
  setSelectedPlan: (item: PricingModelItem) => void;
  onHide: () => void;
  hideOnSuccess?: boolean;
  hideExpandButton?: boolean;
  onSuccess: () => void;
  backOptionsText?: string;
  paymentMethods?: PaymentMethod[] | null;
  reload: () => void;
  secondaryPaymentAction?: ReactNode;
}

function PurchasePlan(props: UpgradePlanProps) {
  const {
    reload,
    selectedPlan,
    pricingModel,
    onHide,
    onSuccess,
    setSelectedPlan,
    hideExpandButton,
    backOptionsText,
    paymentMethods,
    hideOnSuccess,
  } = props;
  const showModes = useMemo(
    () => paymentMethods && paymentMethods.length,
    [paymentMethods]
  );
  const [mode, setMode] = useState<"on-file" | "new">(() =>
    showModes ? "on-file" : "new"
  );
  const [showIncluded, setShowIncluded] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const user = useUser(true);

  const onPurchaseSuccess = () => {
    if (user.student) {
      updateUser({
        ...user,
        student: {
          ...user.student,
          has_pro_access: true,
        },
      });
    }

    if (hideOnSuccess) {
      onHide();
    }
    onSuccess();
  };

  const hdUpgrade = () => {
    navigate(TalaeraRoutes.buildPlanDetailsPath());
  };

  return (
    <div className={"purchase-plan-component plan-detail-upgrade-row"}>
      <HStack className={"back-container"} justify={"space-between"}>
        {!!backOptionsText && (
          <HStack align={"center"} gap={8} events={{ onClick: () => onHide() }}>
            <ArrowBackIcon />
            <div>{backOptionsText}</div>
          </HStack>
        )}

        {!!backOptionsText && <div onClick={() => onHide()}>{t("Common.Cancel")}</div>}
      </HStack>

      <HStack className={"payment-container"} justify={"space-between"}>
        <div className={"price-box"}>
          {pricingModel.type === PricingModelType.SELF_STUDY && (
            <div>{t("Common.choosePayment")}</div>
          )}
          {pricingModel.type === PricingModelType.ONE_ON_ONE && (
            <div>{t("Common.chooseSessionPackage")}</div>
          )}
          <PlanSelector
            pricingModel={pricingModel!}
            selectedPlan={selectedPlan!}
            setSelectedPlan={setSelectedPlan}
          />
          <div className={"see-all"}>
            {pricingModel.type === PricingModelType.SELF_STUDY && (
              <ComparePlansModal
                title={t("Common.Compare plans")}
                enable
                onUpgrade={hdUpgrade}
              >
                <Action className={"btn-compare-plan"}>
                  {t("Common.Compare plans")}
                </Action>
              </ComparePlansModal>
            )}

            {!hideExpandButton && pricingModel.type === PricingModelType.ONE_ON_ONE && (
              <ExpandableWhatIncludedButton
                expanded={showIncluded}
                onClick={() => setShowIncluded(prev => !prev)}
              >
                <VStack className={"included-content"} gap={10}>
                  <HStack align={"center"} className={"include-plan-item"}>
                    <CheckIcon /> {t("Common.personalized11")}
                  </HStack>
                  <HStack align={"center"} className={"include-plan-item"}>
                    <CheckIcon /> {t("Common.expertEnglish")}
                  </HStack>
                  <HStack align={"center"} className={"include-plan-item"}>
                    <CheckIcon /> {t("Common.flexibleScheduling")}
                  </HStack>
                  <HStack align={"center"} className={"include-plan-item"}>
                    <CheckIcon /> {t("Common.accessTo")}
                  </HStack>
                </VStack>
              </ExpandableWhatIncludedButton>
            )}
          </div>
        </div>
        <div className="add-card">
          {!!showModes && (
            <RadioGroup
              className="mode-selector"
              row
              defaultValue={"on-file"}
              value={mode}
              onChange={e => setMode(e?.target?.value === "on-file" ? "on-file" : "new")}
            >
              <FormControlLabel
                value={"on-file"}
                control={
                  <Radio
                    className={classes.root}
                    disableRipple
                    color="default"
                    checkedIcon={
                      <span className={`${classes.icon} ${classes.checkedIcon}`} />
                    }
                    icon={<span className={classes.icon} />}
                  />
                }
                label={t("Common.Payment on File")}
              />
              <FormControlLabel
                value={"new"}
                control={
                  <Radio
                    className={classes.root}
                    disableRipple
                    color="default"
                    checkedIcon={
                      <span className={`${classes.icon} ${classes.checkedIcon}`} />
                    }
                    icon={<span className={classes.icon} />}
                  />
                }
                label={t("Common.Credit/Debit Card")}
              />
            </RadioGroup>
          )}

          {mode === "on-file" && (
            <ExistingPaymentMethods
              stripePromise={stripePromise}
              paymentMethods={paymentMethods!}
              pricingModelItem={selectedPlan}
              onSuccess={onPurchaseSuccess}
              reload={reload}
              secondaryPaymentAction={props.secondaryPaymentAction}
            />
          )}
          {mode === "new" && (
            <Elements stripe={stripePromise}>
              {/* eslint-disable-next-line no-console */}
              <AddNewPaymentMethod
                secondaryPaymentAction={props.secondaryPaymentAction}
                reload={reload}
                plan={selectedPlan!}
                onSuccess={onPurchaseSuccess}
                onDismiss={() => onHide()}
              />
            </Elements>
          )}
        </div>
      </HStack>
    </div>
  );
}
export default PurchasePlan;
